import { FirebaseError } from 'firebase/app';
import {
  firestorePaths,
  InventoryAuthority,
  requireOperationId,
} from '@qkindex/contracts';
import { validateItemImage } from '../../services/imageProcessing';
import { rapidCaptureDb } from './rapidCaptureDb';
import {
  RapidCaptureRecord,
  sanitizeRapidCaptureTerminalRecord,
} from './rapidCaptureTypes';
import { captureService, CaptureStatus } from '../../services/captureService';
import { invalidateInventory } from '../collection/inventoryInvalidation';

const errorDetails = (
  error: unknown,
): NonNullable<RapidCaptureRecord['lastError']> => {
  const code =
    error instanceof FirebaseError
      ? error.code
      : typeof (error as { code?: unknown })?.code === 'string'
        ? (error as { code: string }).code
        : '';
  const message =
    error instanceof Error ? error.message : 'The queued operation failed.';
  const details = (
    error as { details?: { code?: unknown; retryAfterSeconds?: unknown } }
  )?.details;
  const retryAfterSeconds =
    typeof details?.retryAfterSeconds === 'number'
      ? details.retryAfterSeconds
      : undefined;
  if (details?.code === 'collection-unavailable')
    return {
      category: 'capacity',
      message:
        'This collection cannot accept uploads right now. Check the owner’s billing balance and collection availability, then retry. Your capture is kept on this device.',
    };
  if (details?.code === 'capacity-exceeded') {
    return {
      category: 'capacity',
      message:
        'This collection has reached its operational item limit. The captured draft was kept.',
    };
  }
  if (
    code === 'validation' ||
    code === 'conflict' ||
    code.includes('invalid-argument') ||
    [
      'confirmation-required',
      'operation-conflict',
      'invalid-input',
      'insufficient-credits',
    ].includes(String(details?.code))
  )
    return { category: 'conflict', message };
  if (code.includes('unauthenticated'))
    return { category: 'authentication', message };
  if (code.includes('permission')) return { category: 'permission', message };
  if (code.includes('resource-exhausted') || code.includes('quota'))
    return { category: 'quota', message, retryAfterSeconds };
  if (code.includes('network') || !navigator.onLine)
    return { category: 'network', message };
  if (message.toLocaleLowerCase().includes('already in use'))
    return { category: 'conflict', message };
  return { category: 'unknown', message };
};

const update = async (
  record: RapidCaptureRecord,
  changes: Partial<RapidCaptureRecord>,
) => {
  return rapidCaptureDb.update(record, (current) =>
    current.state === 'confirming' ? current : { ...current, ...changes },
  );
};

export const rapidCaptureAuthority = (
  record: RapidCaptureRecord,
): InventoryAuthority & { membershipVersion: number } => {
  if (
    record.schemaVersion !== 5 ||
    !record.userId ||
    !record.collectionId ||
    !Number.isSafeInteger(record.membershipVersion) ||
    record.membershipVersion < 0 ||
    record.compatibilityMode !== 'collection-only' ||
    ![
      'pending',
      'submitting',
      'analysis-submitting',
      'confirming',
      'failed',
      'terminal',
    ].includes(record.state) ||
    typeof record.itemId !== 'string' ||
    !record.itemId ||
    !(record.image instanceof Blob) ||
    typeof record.keywords !== 'string' ||
    typeof record.location !== 'string' ||
    !Number.isFinite(record.qty) ||
    record.qty < 1 ||
    typeof record.itemCreated !== 'boolean' ||
    !Number.isSafeInteger(record.attempts) ||
    record.attempts < 0 ||
    typeof record.createdAt !== 'string' ||
    typeof record.updatedAt !== 'string' ||
    (record.acceptedAiCostVersion !== undefined &&
      (record.compatibilityMode !== 'collection-only' ||
        !record.analysisOperationId ||
        typeof record.acceptedAiCostVersion !== 'string' ||
        record.acceptedAiCostVersion.length < 1 ||
        record.acceptedAiCostVersion.length > 128)) ||
    (record.analyzeImage === true &&
      (!record.analysisOperationId ||
        !record.acceptedAiCostVersion ||
        record.analysisOperationId === record.operationId)) ||
    (record.serverAccepted !== undefined &&
      typeof record.serverAccepted !== 'boolean') ||
    (record.controlAction !== undefined &&
      !['retry', 'save'].includes(record.controlAction)) ||
    (record.pendingAiCostVersion === undefined) !==
      (record.pendingAiCredits === undefined) ||
    (record.pendingAiCostVersion !== undefined &&
      (record.aiSkipReason !== 'cost-changed' ||
        typeof record.pendingAiCostVersion !== 'string' ||
        record.pendingAiCostVersion.length < 1 ||
        record.pendingAiCostVersion.length > 128 ||
        !Number.isSafeInteger(record.pendingAiCredits) ||
        (record.pendingAiCredits as number) < 1))
  ) {
    throw new Error('Queued collection authority is invalid.');
  }
  try {
    requireOperationId(record.operationId);
    if (record.analysisOperationId !== undefined) {
      requireOperationId(record.analysisOperationId);
    }
    firestorePaths.member(record.collectionId, record.userId);
    firestorePaths.item(record.collectionId, record.itemId);
  } catch {
    throw new Error('Queued collection authority is invalid.');
  }
  return {
    userId: record.userId,
    collectionId: record.collectionId,
    compatibilityMode: record.compatibilityMode,
    membershipVersion: record.membershipVersion,
  };
};

export const applyCaptureStatus = async (
  record: RapidCaptureRecord,
  result: CaptureStatus,
  acknowledgedControlAction?: RapidCaptureRecord['controlAction'],
) => {
  if (result.itemId !== undefined && result.itemId !== record.itemId)
    throw new Error('The saved item receipt does not match this capture.');
  const complete = result.state === 'complete';
  let newlyComplete = false;
  await rapidCaptureDb.update(record, (current) => {
    // A no-op Retry can acknowledge the known revision. Pushed snapshots cannot
    // acknowledge a pending control, nor can a Retry response acknowledge Save.
    const retryAcknowledged =
      acknowledgedControlAction === 'retry' &&
      current.controlAction === 'retry';
    if (
      current.state === 'confirming' ||
      (current.serverRevision !== undefined &&
        (result.revision === undefined ||
          result.revision < current.serverRevision)) ||
      (current.controlAction &&
        result.revision !== undefined &&
        result.revision === current.serverRevision &&
        !complete &&
        !retryAcknowledged)
    )
      return current;
    if (result.state === 'discarded') return undefined;
    newlyComplete = complete;
    return {
      ...current,
      serverAccepted: true,
      ...(result.revision === undefined
        ? {}
        : { serverRevision: result.revision }),
      controlAction: undefined,
      ...(current.controlAction === 'save' || result.mode === 'save'
        ? {
            analyzeImage: false,
            pendingAiCostVersion: undefined,
            pendingAiCredits: undefined,
          }
        : {}),
      itemCreated: complete,
      state: complete
        ? 'confirming'
        : result.state === 'failed'
          ? 'failed'
          : 'analysis-submitting',
      completion: complete
        ? result.analysisComplete
          ? 'analysis-complete'
          : 'item'
        : undefined,
      aiSettlement: result.aiSettlement,
      aiSkipReason:
        result.errorCode === 'confirmation-required'
          ? 'cost-changed'
          : undefined,
      lastError:
        result.state === 'failed'
          ? {
              category: 'unknown',
              message:
                result.errorMessage ||
                (current.analyzeImage
                  ? 'Keyword generation failed.'
                  : 'The item could not be saved.'),
            }
          : undefined,
      nextAttemptAt: undefined,
    };
  });
  if (newlyComplete)
    invalidateInventory({
      userId: record.userId,
      collectionId: record.collectionId,
      resources: ['items'],
      reloadItemIds: [record.itemId],
    });
};

export const processRapidCaptureRecord = async (
  initial: RapidCaptureRecord,
  shouldContinue: () => boolean = () => true,
  authenticatedUserId: string = initial.userId,
): Promise<void> => {
  if (
    initial.state === 'confirming' ||
    initial.state === 'terminal' ||
    (initial.serverAccepted && !initial.controlAction) ||
    initial.userId !== authenticatedUserId
  )
    return;
  let record = initial;
  try {
    if ((record as { schemaVersion: number }).schemaVersion !== 5) {
      await update(record, {
        state: 'failed',
        lastError: {
          category: 'conflict',
          message:
            'This older capture needs explicit recovery. Its photo is retained; old AI work will not be submitted again.',
        },
      });
      return;
    }
    rapidCaptureAuthority(record);
    if (!shouldContinue()) return;
    let result: CaptureStatus;
    if (record.serverAccepted) {
      if (!record.controlAction) return;
      result = await captureService.control(record, record.controlAction);
    } else {
      await validateItemImage(record.image);
      if (!shouldContinue()) return;
      const submitting = await update(record, {
        state: 'submitting',
        itemCreateAttempted: true,
        attempts: record.attempts + 1,
        lastError: undefined,
      });
      if (!submitting || submitting.state === 'confirming') return;
      record = submitting;
      if (!shouldContinue()) return;
      result = await captureService.submit(record);
      if (record.controlAction && shouldContinue())
        result = await captureService.control(record, record.controlAction);
    }
    if (shouldContinue())
      await applyCaptureStatus(record, result, record.controlAction);
  } catch (error) {
    if (!shouldContinue()) return;
    const details = errorDetails(error);
    const contractCode = (error as { details?: { code?: unknown } })?.details
      ?.code;
    if (
      details.category === 'permission' ||
      ['role-required', 'membership-required', 'collection-not-found'].includes(
        String(contractCode),
      )
    ) {
      await rapidCaptureDb.update(record, (current) =>
        current.state === 'confirming'
          ? current
          : sanitizeRapidCaptureTerminalRecord(
              current,
              contractCode === 'role-required'
                ? 'membership-changed'
                : 'revoked',
              details,
            ),
      );
      return;
    }
    const retryable = ['network', 'unknown', 'quota'].includes(
      details.category,
    );
    await update(record, {
      state: retryable ? 'pending' : 'failed',
      lastError: details,
      aiSkipReason:
        contractCode === 'confirmation-required' ? 'cost-changed' : undefined,
      nextAttemptAt: retryable
        ? new Date(Date.now() + 5000).toISOString()
        : undefined,
    });
  }
};
